import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type {
  LogicalAlignment,
  WithInherit,
  PhysicalAlignment,
} from '@wix/editor-elements-common-utils';
import {
  getScaledFont,
  withFallbackValues,
  castFromInheritIfNeeded,
  convertPhysicalInputAlignmentToLogical,
  convertPhysicalInputAlignmentToDirection,
  addUnitToEveryField,
  parseValueWithUnit,
  getOppositeAlign,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';
import type { Spx } from '@wix/editor-elements-types/thunderbolt';
import type { ComboBoxInputMapperDefinition } from '@wix/editor-elements-library/src/components/ComboBoxInput/ComboBoxInput.types';
import type {
  IComboBoxInputListModalCSSVars,
  TextPaddingListModalCssVars,
} from '../ComboBoxInputListModal.types';
import { migratedFields } from '../constants';

const justifyContent = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
} as const;

type TextPaddingListModalCssNumericVars = Record<
  keyof TextPaddingListModalCssVars,
  number
>;

const getTextPadding = (
  alignment: LogicalAlignment,
  textPadding: number | string,
): TextPaddingListModalCssNumericVars => {
  const { value } = parseValueWithUnit(String(textPadding), {
    fallbackValue: { value: 0, unit: 'px' },
  });

  if (alignment === 'center') {
    return {
      '--textPaddingDropDown_start': 0,
      '--textPaddingDropDown_end': 0,
    };
  }

  return {
    [`--textPaddingDropDown_${alignment}`]: value,
    [`--textPaddingDropDown_${getOppositeAlign(alignment)}`]: 0,
  } as TextPaddingListModalCssNumericVars;
};

export const css = withCompInfo<
  IComboBoxInputListModalCSSVars,
  ComboBoxInputMapperDefinition,
  {}
>()(
  [
    'compData',
    'compProps',
    'styleProperties',
    'compLayout',
    'hasResponsiveLayout',
    'isMobileView',
    'siteFonts',
    'formatCssValue',
    'isOneDocMigrated',
  ],
  ({
    compData,
    compProps,
    styleProperties,
    compLayout,
    hasResponsiveLayout,
    isMobileView,
    siteFonts,
    formatCssValue,
    isOneDocMigrated,
  }) => {
    const { direction, dropdownDirection = 'inherit' } = compData;
    const { spx, align, dropdownAlign, dropdownItemsSpacing, textPadding } =
      styleProperties as Record<string, string> & {
        spx?: Spx;
        align: LogicalAlignment;
        dropdownAlign: WithInherit<LogicalAlignment>;
        dropdownItemsSpacing: number;
        textPadding: number;
      };
    const compInfo = {
      compLayout: isOneDocMigrated ? {} : compLayout,
      styleProperties,
      siteFonts,
      isMobileView,
    };
    const { inputMobileFontSize } = compProps;

    const calculatedDropdownAlign = castFromInheritIfNeeded(
      dropdownAlign,
      align,
    );

    const dropdownOptionJustifyContent =
      justifyContent[calculatedDropdownAlign as keyof typeof justifyContent];

    const dropdownItemsSpacingValue = dropdownItemsSpacing ?? 16;

    const textPaddingValues = getTextPadding(
      calculatedDropdownAlign,
      textPadding,
    );

    const textPaddingVars = addUnitToEveryField(
      textPaddingValues,
      textPadding,
      {
        formatCssValueWithSpx: formatCssValue,
        spx,
      },
    );

    return {
      height: '100%',
      '--align': align,
      '--direction': withSiteDirection(direction),
      '--dropdownDirection': dropdownDirection,
      '--fnt': getScaledFont(compInfo, 'fnt', inputMobileFontSize),
      '--dropdownItemsSpacing': formatCssValue(dropdownItemsSpacingValue, spx),
      '--dropdownOptionJustifyContent': dropdownOptionJustifyContent,
      ...textPaddingVars,
      ...(!hasResponsiveLayout && { width: 'initial' }),
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: migratedFields,
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'textAlignment', target: 'align' }],
        enhancer: textAlignment =>
          convertPhysicalInputAlignmentToLogical(
            textAlignment as PhysicalAlignment,
          ),
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'textAlignment', target: 'direction' }],
        enhancer: textAlignment => {
          return convertPhysicalInputAlignmentToDirection(
            textAlignment as PhysicalAlignment,
          );
        },
      },
    ]),
  ],
);

export default createComponentMapperModel({ css });
